import { Injectable } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { BehaviorSubject, delay, Observable, tap } from 'rxjs';
import { FormLayoutService } from 'src/app/shared/components/form-layout/form-layout.service';
import { booleanOr } from 'src/app/shared/lib/observable.helpers';
import { PublicMeetingService } from 'src/app/shared/services/public-meeting.service';
import { RsvpEventService } from 'src/app/shared/services/rsvp-event.service';
import { UrlParamsService } from 'src/app/shared/services/url-params.service';
import { FormSettingsDTO } from 'src/app/shared/types/FormSettingsDTO';
import { PublicMeetingDTO } from 'src/app/shared/types/PublicMeetingDTO';
import { PublicOnlineMeetingDTO } from 'src/app/shared/types/PublicOnlineMeetingsDTO';
import { RsvpDisplay, RsvpDisplayControls } from 'src/app/shared/types/RsvpDisplay';
import { RegistrationService } from '../registration/registration.service';

const DEFAULT_STRING_VALIDATORS = [Validators.required, Validators.minLength(1)];
const DEFAULT_NUMBER_VALIDATORS = [Validators.required, Validators.min(1)];

const DEFAULT_FORM_SETTINGS: FormSettingsDTO = {
  ForeignLanguages: '',
  ProjectFooter: '',
  ProjectLogoUrl: '',
  EntityLogoUrl: '',
  BackgroundImageUrl: '/assets/images/default-background.jpeg',
  ShowEntityLogo: false,
  ShowProjectLogo: false,
  EntityName: '',
  BrandingColor: '',
};

@Injectable({
  providedIn: 'root',
})
export class RsvpPageService {
  private _fetchingMeetingInfo$ = new BehaviorSubject(true);
  public currentMeetingInfo: Observable<PublicMeetingDTO | PublicOnlineMeetingDTO> | undefined;

  private _isPostInProgress$ = new BehaviorSubject(false);
  /**
   * Global observable for tracking whether the client
   * is awaiting a fetch call for 'postComment'.
   */
  public isPostInProgress$ = this._isPostInProgress$.asObservable();

  constructor(
    private _rsvpService: RsvpEventService,

    private _urlParamsService: UrlParamsService,
    private _registrationService: RegistrationService,
    private _publicMeetingService: PublicMeetingService,
    private _formLayoutService: FormLayoutService,
    private formBuilder: FormBuilder
  ) {}

  setupProjectLayoutInfo() {
    const meetingGuid = this._urlParamsService.meetingGuid;
    if (!meetingGuid) {
      return undefined;
    }

    return this._registrationService.getInPersonMeetingInfo(meetingGuid).pipe(
      tap(() => this._fetchingMeetingInfo$.next(true)),
      delay(300),
      tap({
        next: (res) => {
          const settings = res.Settings;
          if (settings) {
            this._formLayoutService.setFormLayoutSettings(settings);
          }

          this._fetchingMeetingInfo$.next(false);
        },
        error: () => {
          this._fetchingMeetingInfo$.next(false);
        },
      })
    );
  }

  setupFormDisplay(): RsvpDisplay | undefined {
    const meetingGuid = this._urlParamsService.meetingGuid;
    if (!meetingGuid) {
      return undefined;
    }
    const rsvpControls = {
      peopleCount: this.formBuilder.control(1, {
        validators: DEFAULT_NUMBER_VALIDATORS,
      }),
    } as const satisfies RsvpDisplayControls;

    const rsvpDisplay = {
      fg: this.formBuilder.group(rsvpControls),
      controls: rsvpControls,

      publicMeeting$: this._publicMeetingService.getInPersonMeetingByGuid(meetingGuid).pipe(
        tap((given) => {
          this._fetchingMeetingInfo$.next(true);
          this._updateLayoutData(given);
        }),
        delay(300),
        tap({
          next: () => {
            this._fetchingMeetingInfo$.next(false);
          },
          error: () => {
            this._fetchingMeetingInfo$.next(false);
          },
        })
      ),

      isLoading$: booleanOr(this._rsvpService.isPostInProgress$, this._fetchingMeetingInfo$),
      completed$: new BehaviorSubject<boolean>(false),
    } satisfies RsvpDisplay;

    rsvpDisplay.isLoading$.subscribe({
      next: (value) => {
        if (value) {
          rsvpDisplay.fg.disable();
        } else {
          rsvpDisplay.fg.enable();
        }
      },
    });

    return rsvpDisplay;
  }

  post(meetingGuid: string, stakeGuid: string, totalAttendees: number) {
    return this._rsvpService
      .postReservationFromDisplay(meetingGuid, stakeGuid, totalAttendees)
      .pipe(
        tap({
          subscribe: () => {
            this._isPostInProgress$.next(true);
          },
          complete: () => {
            this._fetchingMeetingInfo$.next(false);
          },
          error: () => {
            this._fetchingMeetingInfo$.next(false);
          },
        })
      );
  }

  private _updateLayoutData(given: PublicMeetingDTO) {
    if (!given.Settings) {
      return;
    }

    this._formLayoutService.setFormLayoutSettings({
      BackgroundImageUrl:
        given.Settings.BackgroundImageUrl ?? DEFAULT_FORM_SETTINGS.BackgroundImageUrl,
      ForeignLanguages: given.Settings.ForeignLanguages ?? DEFAULT_FORM_SETTINGS.ForeignLanguages,
      EntityLogoUrl: given.Settings.EntityLogoUrl ?? DEFAULT_FORM_SETTINGS.EntityLogoUrl,
      EntityName: given.Settings.EntityName ?? DEFAULT_FORM_SETTINGS.EntityName,
      ProjectFooter: given.Settings.ProjectFooter ?? DEFAULT_FORM_SETTINGS.ProjectFooter,
      ProjectLogoUrl: given.Settings.ProjectLogoUrl ?? DEFAULT_FORM_SETTINGS.ProjectLogoUrl,
      ShowEntityLogo: given.Settings.ShowEntityLogo ?? DEFAULT_FORM_SETTINGS.ShowEntityLogo,
      ShowProjectLogo: given.Settings.ShowProjectLogo ?? DEFAULT_FORM_SETTINGS.ShowProjectLogo,
      BrandingColor: given.Settings.BrandingColor ?? DEFAULT_FORM_SETTINGS.BrandingColor,
    });
  }
}
